import { type CellComponent, ColumnDefinition, Options } from '@/types/tabulator';
/**
 * FERP Portal - Shared Tabulator Configuration
 *
 * Centralized Tabulator options, column helpers, and formatter factories.
 * Every Tabulator instance in the portal should use these defaults for
 * consistent appearance and behavior.
 *
 * Usage:
 *   import { TABULATOR_DEFAULTS, formatters, columnHelpers } from '@/utils/tabulatorConfig';
 *   const instance = new Tabulator(el, { ...TABULATOR_DEFAULTS, data, columns });
 *
 * FERP Principles:
 * - Consistent UI across all tables
 * - Mantine theme integration (CSS vars, not inline styles)
 * - Data-driven: columns from metadata when possible
 */

// ============================================================================
// Default Tabulator options — merge these with per-table overrides
// ============================================================================

export const TABULATOR_DEFAULTS: Partial<Options> = {
  layout: 'fitColumns',
  pagination: true,
  paginationMode: 'local',
  paginationSize: 25,
  paginationSizeSelector: [25, 50, 100, 200],
  responsiveLayout: 'collapse',
  movableColumns: true,
  height: '600px',
  placeholder: 'No data found',
  // Accessibility
  headerSortClickElement: 'icon',
};

// ============================================================================
// Column definition helpers
// ============================================================================

/** Convert camelCase/snake_case to human-readable title */
export function humanizeFieldName(field: string): string {
  return field
    .replace(/([A-Z])/g, ' $1')
    .replace(/_/g, ' ')
    .replace(/^\w/, (c: string) => c.toUpperCase())
    .trim();
}

/** Standard text column with header filter */
export function textColumn(field: string, overrides?: Partial<ColumnDefinition>): ColumnDefinition {
  return {
    title: humanizeFieldName(field),
    field,
    sorter: 'string',
    headerFilter: 'input',
    headerFilterPlaceholder: `Filter ${humanizeFieldName(field).toLowerCase()}...`,
    ...overrides,
  };
}

/** Badge column using FERP CSS classes */
export function badgeColumn(
  field: string,
  colorMap: Record<string, string>,
  overrides?: Partial<ColumnDefinition>,
): ColumnDefinition {
  return {
    title: humanizeFieldName(field),
    field,
    sorter: 'string',
    width: 110,
    formatter: (cell: CellComponent) => {
      const val = cell.getValue() as string;
      const cssClass = colorMap[val] ?? 'normal';
      return `<span class="ferp-tbl-badge ferp-tbl-badge--${cssClass}">${val}</span>`;
    },
    ...overrides,
  };
}

/** Priority column with standard FERP color coding */
export function priorityColumn(overrides?: Partial<ColumnDefinition>): ColumnDefinition {
  return badgeColumn('priority', {
    critical: 'critical',
    urgent: 'urgent',
    high: 'high',
    normal: 'normal',
    low: 'low',
  }, { width: 100, ...overrides });
}

/** Type badge column (built-in vs saved, etc.) */
export function typeBadgeColumn(
  field: string,
  badgeMap: Record<string, string>,
  overrides?: Partial<ColumnDefinition>,
): ColumnDefinition {
  return {
    title: humanizeFieldName(field),
    field,
    sorter: 'string',
    width: 100,
    formatter: (cell: CellComponent) => {
      const val = cell.getValue() as string;
      const cssClass = badgeMap[val] ?? 'normal';
      return `<span class="ferp-tbl-badge ferp-tbl-badge--${cssClass}">${val}</span>`;
    },
    ...overrides,
  };
}

/** Actions column with FERP-styled buttons */
export function actionsColumn(
  buttons: Array<{ label: string; cssClass: string; className: string }>,
  onClick: (e: Event, cell: CellComponent) => void,
  overrides?: Partial<ColumnDefinition>,
): ColumnDefinition {
  return {
    title: 'Actions',
    field: '_actions',
    width: 150,
    hozAlign: 'center' as const,
    headerSort: false,
    formatter: () => {
      return buttons
        .map(
          (b) =>
            `<button class="ferp-tbl-action ferp-tbl-action--${b.cssClass} ${b.className}" title="${b.label}">${b.label}</button>`,
        )
        .join('');
    },
    cellClick: (e: any, cell: any) => onClick(e as unknown as Event, cell),
    ...overrides,
  };
}

/** Status column with dropdown filter */
export function statusColumn(
  statuses: Record<string, string>,
  overrides?: Partial<ColumnDefinition>,
): ColumnDefinition {
  return {
    title: 'Status',
    field: 'status',
    sorter: 'string',
    width: 130,
    headerFilter: 'list',
    headerFilterParams: { values: { '': 'All', ...statuses } },
    headerFilterPlaceholder: 'Filter status...',
    ...overrides,
  };
}

/** Timestamp column */
export function timestampColumn(field: string, overrides?: Partial<ColumnDefinition>): ColumnDefinition {
  return {
    title: humanizeFieldName(field),
    field,
    sorter: 'string',
    width: 140,
    ...overrides,
  };
}

// ============================================================================
// Group-by support
// ============================================================================

/**
 * System fields available for groupBy in the table toolbar.
 * DRR-141: lifecyclePhase added for subject-scoped views.
 *
 * These are the five fields common across all 25 primitives.
 * Primitive-specific groupable fields will be added when the backend
 * exposes `groupable: bool` on GqlListColumn (FormSchema API). At that
 * point, TabulatorPrimitiveManager will read groupable fields from the
 * schema instead of this static list.
 */
export const GROUPABLE_SYSTEM_FIELDS: ReadonlyArray<{ field: string; label: string }> = [
  { field: 'status', label: 'Status' },
  { field: 'safetyLevel', label: 'Safety Level' },
  { field: 'active', label: 'Active/Inactive' },
  { field: 'createdBy', label: 'Created By' },
  { field: 'lifecyclePhase', label: 'Lifecycle Phase' },
  { field: 'taskGroupRef', label: 'Work Type' },
  { field: 'createdBySource', label: 'Source' },
  { field: 'subjectActorId', label: 'Subject' },
  { field: 'assignedActorId', label: 'Assigned To' },
];

/** Tabulator groupHeader formatter — used with Tabulator's groupBy option.
 * Accepts an optional valueResolver to translate config IDs to display names
 * (e.g., "tg-4-3-001" → "Daily Care & Operations"). */
export function groupHeaderFormatter(value: string, count: number): string {
  return `<span style="font-weight:600;margin-left:4px">${value || '(none)'}</span> <span style="color:var(--mantine-color-dimmed);margin-left:8px">${count} items</span>`;
}

/** Create a group header formatter with a value resolver.
 * The resolver translates raw group values (config IDs) to display names. */
export function createGroupHeaderFormatter(
  resolver: (value: string) => string,
): (value: string, count: number) => string {
  return (value: string, count: number) => {
    const display = resolver(value) || value || '(none)';
    return `<span style="font-weight:600;margin-left:4px">${display}</span> <span style="color:var(--mantine-color-dimmed);margin-left:8px">${count} items</span>`;
  };
}

/** Task group config ID pattern (e.g., "tg-4-3-001", "0-0-10") */
const TASK_GROUP_ID_PATTERN = /^(tg-)?(\d+-)+\d+$/;

/**
 * Create a compound group header formatter for multi-level grouping.
 * Detects value type and applies appropriate resolution:
 * - Task group IDs (tg-X-Y-Z or X-Y-Z) → resolved via taskGroupResolver
 * - Enum values (lowercase/snake_case) → humanized (e.g., "scheduled" → "Scheduled")
 * - Other values → displayed as-is
 *
 * DRR-178 Phase 4: Compound scoping support.
 */
export function createCompoundGroupHeaderFormatter(
  taskGroupResolver: (value: string) => string,
): (value: string, count: number) => string {
  return (value: string, count: number) => {
    if (!value) {
      return `<span style="font-weight:600;margin-left:4px">(none)</span> <span style="color:var(--mantine-color-dimmed);margin-left:8px">${count} items</span>`;
    }

    let display: string;

    // Check if it's a task group config ID
    if (TASK_GROUP_ID_PATTERN.test(value)) {
      display = taskGroupResolver(value);
      // If resolver returns "(no group)" or the same value, try humanizing
      if (display === '(no group)' || display === value) {
        display = value;
      }
    } else {
      // For enum-like values (lowercase, snake_case), humanize them
      // e.g., "scheduled" → "Scheduled", "in_progress" → "In Progress"
      display = humanizeFieldName(value);
    }

    return `<span style="font-weight:600;margin-left:4px">${display}</span> <span style="color:var(--mantine-color-dimmed);margin-left:8px">${count} items</span>`;
  };
}
